import type { ReferencedMultiChartDataPoint } from "@wayflyer/flyui-dataviz";

import { INTERNAL_REV_METRIC } from "../constants";
import type { RevenueSummaryItem } from "../revenue-datapoints";

const numericValue = (value: unknown): number | null => {
  if (value == null || value === "") {
    return null;
  }
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

export const revenueLinePoints = (
  data: ReferencedMultiChartDataPoint[],
  group: string,
  period: "Current" | "Reference"
) =>
  data.map((row) => ({
    x: Date.parse(`${row.dayRaw}T00:00:00Z`),
    y: numericValue(row[`${group}${INTERNAL_REV_METRIC}${period}`]),
    datum: row,
  }));

export const revenueCompositionValues = (data: ReferencedMultiChartDataPoint[], group: string) =>
  data.map((row) => ({
    category: String(row.dayRaw),
    y: numericValue(row[`${group}${INTERNAL_REV_METRIC}Current`]),
    datum: row,
  }));

const NAMED_COMPOSITION_STORES = 6;

export const getRevenueComposition = (
  summaryData: RevenueSummaryItem[],
  selectedGroups: string[],
  percentageData: ReferencedMultiChartDataPoint[]
) => {
  const rankedStores = summaryData
    .filter((item) => item.metricType !== "Total")
    .map((item, index) => ({
      group: item.metricType,
      revenue: item.rawMetricValue ?? 0,
      index,
    }))
    .sort((a, b) => b.revenue - a.revenue || a.index - b.index);
  const selectedStores = rankedStores.filter((store) => selectedGroups.includes(store.group));
  // Rank before filtering so colours and Other membership stay stable during selection.
  const namedStores = rankedStores
    .slice(0, NAMED_COMPOSITION_STORES)
    .map((store, colorIndex) => ({ ...store, colorIndex }))
    .filter((store) => selectedGroups.includes(store.group));
  const otherStores = rankedStores
    .slice(NAMED_COMPOSITION_STORES)
    .filter((store) => selectedGroups.includes(store.group));
  const series = namedStores.map((store) => ({
    key: `store:${store.group}`,
    label: `${store.group} This Year`,
    colorIndex: store.colorIndex,
    data: revenueCompositionValues(percentageData, store.group),
  }));
  const otherData = percentageData.map((row) => {
    const values = otherStores
      .map((store) => numericValue(row[`${store.group}${INTERNAL_REV_METRIC}Current`]))
      .filter((value): value is number => value != null);
    return {
      category: String(row.dayRaw),
      y: values.length ? values.reduce((total, value) => total + value, 0) : null,
      datum: row,
    };
  });
  if (otherStores.length) {
    series.push({
      key: "other-stores",
      label: "Other",
      colorIndex: NAMED_COMPOSITION_STORES,
      data: otherData,
    });
  }
  const tooltipRows = new Map(
    percentageData.map((row, index) => {
      const rows = selectedStores.flatMap((store) => {
        const value = numericValue(row[`${store.group}${INTERNAL_REV_METRIC}Current`]);
        return value == null ? [] : [{ key: `store:${store.group}`, label: `${store.group} This Year`, value }];
      });
      if (otherStores.length && otherData[index].y != null) {
        rows.push({ key: "other-stores", label: "Other subtotal", value: otherData[index].y });
      }
      return [String(row.dayRaw), rows] as const;
    })
  );
  return { series, tooltipRows };
};
